import { Position, XYPosition, ReactFlowInstance, useEdges, useReactFlow, Node, Edge } from 'reactflow';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { alpha, Box, Button, ListItemIcon, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import { NodeProps, WorkflowExecutionStatusEnum, ExecutionNodeStatusEnum } from 'types.ts';
import { FaArrowsRotateAnimated, NodeBadge, NodeCard, TestDataIcon } from 'views/flow/canvas/nodes/CustomNode.styles';
import useDialog from 'hooks/useDialog.tsx';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog.tsx';
import {
  WorkflowNodeType,
  WorkflowNode,
  QueryKey,
  WorkflowNodeTestData,
  WorkflowNodeExpectedColumns,
  CreateWorkflowNode,
} from 'api/model.ts';
import { getNodeConfig, getNodeColorByGroup } from 'api/workflows-steps/formSchema';
import { RemoveElementBtn } from '../common/RemoveElementBtn';
import { EdgeHandle } from '../common/EdgeHandle';
import { useStore } from 'store/store.ts';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { FaBan, FaTimesCircle } from 'react-icons/fa';
import { FaCircleCheck, FaCircleQuestion, FaHourglassStart, FaTriangleExclamation } from 'react-icons/fa6';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { getOfflineNodeIssue } from 'config/offlineRestrictions.ts';
import useModels from 'api/prompts-models/useModels';
import { getWorkflowExecution } from 'api/workflows/requests.ts';
import { useQuery } from '@tanstack/react-query';
import { useExecutionStream } from 'api/sse/useExecutionStream';
import LogViewerDialog from 'components/dialog/LogViewerDialog.tsx';
import EditTestDataDialog from 'components/dialog/EditTestDataDialog.tsx';
import { useDrawer } from 'hooks/useDrawer';
import TokenUsageDialog from 'components/dialog/TokenUsageDialog.tsx';
import NodeOutputDialog from '../NodeOutputDialog.tsx';
import CustomNodeDetailsTooltip from 'views/flow/canvas/nodes/CustomNodeDetailsTooltip.tsx';
import { NodeIssueTooltip } from 'views/flow/canvas/nodes/NodeIssueTooltip.tsx';
import { BarChart, Block, ContentCopy, Science, Comment, TableChart, AccountTree, ViewWeek } from '@mui/icons-material';
import EditExpectedColumnsDialog from 'components/dialog/EditExpectedColumnsDialog.tsx';
import { NodeFormEvent } from '../OverCanvasNodeFormView.tsx';

export default function CustomNode({ id, type, data, name, selected }: NodeProps) {
  const nodeErrors = useStore((state) => state.nodeErrors[id]);
  const { isDraggingNode, isConnectingNodes, offlineMode } = useStore();
  const { models, isLoading } = useModels({ enabled: offlineMode });
  const offlineIssue =
    offlineMode && !isLoading ? getOfflineNodeIssue(type as WorkflowNodeType, data.configuration, models) : undefined;

  const [errorToastDisplayed, setErrorToastDisplayed] = useState(false);

  const nodeTypesThatCantBeDisabled: WorkflowNodeType[] = [
    /* file loaders */
    'data_loader_workflow_execution',
    'data_loader_local_file',
    'data_loader_network_share',
    'data_loader_archive_file',
    'data_loader_one_drive',
    'data_loader_amazon_s3',
    'data_loader_azure_blob_storage',
    'data_loader_google_cloud_storage',
  ];
  const inputColumnNames: string[] = ['column_name', 'content_column_name', 'validation_target_column_name'];

  const drawer = useDrawer();
  const { projectId, workflowRevision, workflowExecutionId } = useParams();
  const reactFlow: ReactFlowInstance<WorkflowNode> = useReactFlow();
  const [showConfirmationDialog, StepRemovalConfirmationDialog] = useDialog(ConfirmationDialog);
  const [contextMenuPosition, setContextMenuPosition] = useState<XYPosition | null>(null);
  const [isSelected, setIsSelected] = useState(selected);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const {
    group,
    icon,
    name: nodeTypeName,
    source,
    target,
    definesExpectedColumns,
  } = getNodeConfig(type as WorkflowNodeType)!;
  const color = getNodeColorByGroup(group);
  const navigate = useNavigate();
  const isStepEnabled = data.enabled;
  const isExecution = workflowExecutionId != null;
  const readOnly = workflowRevision != null;
  const isEmbedWorkflowNode = type === 'embed_workflow';
  const theme = useTheme();
  const [showLogs, LogsDialog] = useDialog(LogViewerDialog);
  const [showNodeOutput, OutputDialog] = useDialog(NodeOutputDialog);
  const [showTestData, TestDataDialog] = useDialog(EditTestDataDialog);
  const [showExpectedColumns, ExpectedColumnsDialog] = useDialog(EditExpectedColumnsDialog);
  const [showTokenUsage, ViewTokenUsageDialog] = useDialog(TokenUsageDialog);

  const { data: workflowExecution } = useQuery({
    queryKey: [QueryKey.WorkflowExecutions, { id: workflowExecutionId }],
    queryFn: () => getWorkflowExecution(workflowExecutionId ?? ''),
    enabled: !!workflowExecutionId,
  });

  const nodeName: string = name ?? reactFlow.getNode(id)?.data.name ?? nodeTypeName;
  const edges = useEdges();

  const isValid = useMemo(() => {
    return target && edges.some((e) => e.target == id);
  }, [id, edges, target]);

  const workflowExecutionStatus = workflowExecution?.status;
  const workflowExecutionNodes = workflowExecution?.node_progress;
  const nodeStatusObj = useMemo(
    () => workflowExecutionNodes?.find((node) => node.node_id === id),
    [workflowExecutionNodes, id],
  );
  const isExecutionCleanedUp = workflowExecution?.cleaned_up;
  const nodeStatus = nodeStatusObj?.status;
  const validationError = nodeStatusObj?.result?.validation_error;
  const warnings = nodeStatusObj?.result?.warnings;
  const tokenUsage = nodeStatusObj?.result?.token_usage_metadata;
  const nodeTokenUsage = nodeStatusObj?.result?.node_token_usage_metadata;
  const subworkflowOutputData = nodeStatusObj?.result?.subworkflow_output_data;
  const logFileId = nodeStatusObj?.result?.log_file_id;

  const isNodeCompleted =
    nodeStatus === ExecutionNodeStatusEnum.COMPLETED || nodeStatus === ExecutionNodeStatusEnum.COMPLETED_WITH_WARNINGS;
  const hasWarnings = nodeStatus === ExecutionNodeStatusEnum.COMPLETED_WITH_WARNINGS;
  const isNodeRunning =
    workflowExecutionStatus === WorkflowExecutionStatusEnum.RUNNING && nodeStatus === ExecutionNodeStatusEnum.RUNNING;
  const isNodeNew = nodeStatus === ExecutionNodeStatusEnum.NEW;
  const isNodeFailed = nodeStatus === ExecutionNodeStatusEnum.FAILED;
  const isNodeCancelled = nodeStatus === ExecutionNodeStatusEnum.CANCELLED;
  const isNodeNotExecuted =
    workflowExecutionStatus !== WorkflowExecutionStatusEnum.RUNNING && nodeStatus === ExecutionNodeStatusEnum.NEW;
  const hasOutputTables = (nodeStatusObj?.result?.tables?.length ?? 0) > 0;
  const canShowOutput = isNodeCompleted || ((isNodeFailed || isNodeRunning) && hasOutputTables);

  const executionStatusColor = !isStepEnabled
    ? undefined
    : isNodeCompleted
      ? hasWarnings
        ? theme.palette.warning.main
        : theme.palette.success.main
      : isNodeRunning
        ? theme.palette.info.main
        : isNodeFailed
          ? theme.palette.error.main
          : isNodeCancelled
            ? theme.palette.grey[500]
            : undefined;

  const { data: subworkflowExecution } = useQuery({
    queryKey: [QueryKey.WorkflowExecutions, { id: subworkflowOutputData?.child_execution_id }],
    queryFn: () => getWorkflowExecution(subworkflowOutputData?.child_execution_id ?? ''),
    enabled: !!subworkflowOutputData?.child_execution_id,
  });

  const isSubworkflowRunning = subworkflowExecution?.status === WorkflowExecutionStatusEnum.RUNNING;
  useExecutionStream(isExecution && isSubworkflowRunning ? subworkflowOutputData?.child_execution_id : null);

  const nodeProgress = subworkflowExecution
    ? Number(subworkflowExecution.progress.toFixed(0))
    : nodeStatusObj?.processed_items_percent;

  const onRemoveButtonClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      showConfirmationDialog();
    },
    [showConfirmationDialog],
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuPosition(null);
  }, []);

  const showNodeOutputDetails = useCallback(() => {
    showNodeOutput(true);
    handleCloseContextMenu();
  }, [showNodeOutput, handleCloseContextMenu]);

  const handleShowLogs = useCallback(() => {
    setContextMenuPosition(null);
    showLogs(true);
  }, [showLogs]);

  const handleNodeClick = useCallback(
    (e: MouseEvent) => {
      if (tooltipOpen) {
        setTooltipOpen(false);
      }

      if (e.ctrlKey || e.metaKey) {
        reactFlow.setNodes((nodes) => {
          const node = nodes.find((n) => n.id == id);
          if (node) {
            node.selected = !isSelected;
          }
          return nodes;
        });
        setIsSelected((prev) => !prev);
        return;
      }
      if (isExecution) {
        if (isExecutionCleanedUp) {
          toast.info('Execution node outputs and logs have been cleaned up.');
          return;
        }
        if (isNodeCompleted || (isNodeRunning && hasOutputTables)) {
          showNodeOutputDetails();
        } else if (isNodeFailed) {
          handleShowLogs();
        }
        return;
      }

      const currentNode = reactFlow.getNode(id);
      if (currentNode) {
        const workflowNode: WorkflowNode = {
          ...currentNode.data,
          id: currentNode.id,
          type: currentNode.type as WorkflowNodeType,
        };
        drawer.openNodeForm(workflowNode);
      }
    },
    [
      tooltipOpen,
      isExecution,
      reactFlow,
      id,
      isSelected,
      isNodeCompleted,
      isNodeRunning,
      isNodeFailed,
      hasOutputTables,
      isExecutionCleanedUp,
      showNodeOutputDetails,
      handleShowLogs,
      drawer,
    ],
  );

  const handleUpdateNode = useCallback(
    (formData: CreateWorkflowNode) => {
      if (!reactFlow) {
        return;
      }

      const workflowNode = reactFlow.getNode(formData.id);
      if (workflowNode) {
        workflowNode.data = { ...workflowNode.data, ...formData };
        reactFlow.setNodes((nodes) => [...nodes.filter((n) => n.id != workflowNode.id), workflowNode]);
      }
    },
    [reactFlow],
  );

  const openCenterNodeForm = useCallback(() => {
    const currentNode = reactFlow.getNode(id);
    if (!currentNode) {
      return;
    }

    const workflowNode: WorkflowNode = {
      ...currentNode.data,
      id: currentNode.id,
      type: currentNode.type as WorkflowNodeType,
    };
    const nodeFormEvent = new NodeFormEvent({
      nodeId: workflowNode.id,
      nodeType: workflowNode.type,
      flowInstance: reactFlow,
      onSubmit: handleUpdateNode,
      initialValues: workflowNode,
    });
    document.dispatchEvent(nodeFormEvent);
  }, [reactFlow, id, handleUpdateNode]);

  const handleStepRemoval = useCallback(() => {
    const nodesToRemove = [id];
    const adjacentEdges = edges.filter((e) => e.source === id || e.target === id);
    if (isEmbedWorkflowNode && adjacentEdges.length > 0) {
      // remove proxy nodes connected to the removed embed worklow node
      const allNodes = reactFlow.getNodes();
      nodesToRemove.push(
        ...allNodes
          .filter((n) => adjacentEdges.some((e) => e.source === n.id || e.target === n.id) && n.type === 'proxy')
          .map((n) => n.id),
      );
    }
    reactFlow.setNodes((nodes) => nodes.filter((n) => !nodesToRemove.includes(n.id)));
  }, [edges, id, isEmbedWorkflowNode, reactFlow]);

  const handleNodeContextMenu = useCallback((event: MouseEvent) => {
    event.preventDefault();
    setContextMenuPosition({
      x: event.clientX + 2,
      y: event.clientY - 6,
    });
    event.stopPropagation();
  }, []);

  const handleCopyNode = useCallback(() => {
    const node = {
      id,
      type,
      data,
      position: {
        x: 0,
        y: 0,
      },
    };
    const clipboardData = {
      nodes: [node],
      edges: [],
    };
    localStorage.setItem('clipboard', JSON.stringify(clipboardData));
    toast.success(`Node ${data.name} copied to clipboard`);
    handleCloseContextMenu();
  }, [id, type, data, handleCloseContextMenu]);

  useEffect(() => {
    if (isDraggingNode) {
      setTooltipOpen(false);
    }
  }, [isDraggingNode]);

  useEffect(() => {
    if (isConnectingNodes) {
      setTooltipOpen(false);
    }
  }, [isConnectingNodes]);

  const handleOpenTooltip = useCallback(() => {
    if (!isDraggingNode && !isConnectingNodes) {
      setTooltipOpen(true);
    }
  }, [isDraggingNode, isConnectingNodes]);

  useEffect(() => {
    if (!nodeErrors?.length) {
      setErrorToastDisplayed(false);
      return;
    }

    nodeErrors.forEach((error) => {
      const toastId = id + error;
      const toastContent = (
        <>
          <div
            className="absolute flex items-center justify-center p-1 rounded top-1/2 -left-5 -translate-y-1/2"
            style={{
              backgroundColor: color,
            }}
          >
            {icon}
          </div>
          <Typography variant="subtitle2">{data.name}</Typography>
          <Typography variant="caption">{error}</Typography>
          <div className="flex justify-center w-100">
            <Button size="small" variant="outlined" color="error" onClick={openCenterNodeForm}>
              resolve
            </Button>
          </div>
        </>
      );
      if (toast.isActive(toastId)) {
        toast.update(toastId, {
          render: toastContent,
        });
      } else if (!errorToastDisplayed) {
        toast.error(toastContent, {
          autoClose: false,
          icon: false,
          className: 'overflow-visible gap-1 relative ring-1 ring-red-500/50',
          toastId,
        });
        setErrorToastDisplayed(true);
      }
    });
  }, [nodeErrors, openCenterNodeForm, color, icon, data, id, errorToastDisplayed]);

  function getResultColumn(node: Node<WorkflowNode>): string | null {
    if (node.data.configuration && 'result_column' in node.data.configuration) {
      return node.data.configuration.result_column;
    }
    return null;
  }

  function getSchemaPropertiesKeys(node: Node<WorkflowNode>): string[] {
    if (node.data.configuration) {
      for (const key in node.data.configuration) {
        const schema = node.data.configuration[key as keyof typeof node.data.configuration] as {
          properties?: string;
        };
        if (key.endsWith('_schema') && schema.properties) {
          return Object.keys(schema.properties);
        }
      }
    }
    return [];
  }

  function disableMatchingNodes(
    nodes: Node<WorkflowNode>[],
    resultColumn: string | null,
    schemaPropertiesKeys: string[],
    disabledNodes: string[] = [],
  ): string[] {
    const properties = resultColumn ? [...schemaPropertiesKeys, resultColumn] : schemaPropertiesKeys;

    nodes.forEach((n) => {
      if (
        inputColumnNames.some(
          (columnName) =>
            columnName in n.data.configuration &&
            properties.includes(n.data.configuration[columnName as keyof typeof n.data.configuration]),
        )
      ) {
        if (n.data.enabled) {
          n.data = { ...n.data, enabled: false };
          disabledNodes.push(n.data.name);
        }
        const currentResultColumn = getResultColumn(n);
        const currentSchemaPropertiesKeys = getSchemaPropertiesKeys(n);
        if (currentResultColumn ?? currentSchemaPropertiesKeys) {
          disableMatchingNodes(nodes, currentResultColumn, currentSchemaPropertiesKeys, disabledNodes);
        }
      }
    });
    return disabledNodes;
  }

  function findAllPaths(endNodeId: string, nodes: Node[], edges: Edge[]): string[][] {
    const paths: string[][] = [];
    const rootNodeIds = nodes
      .filter((node) => {
        return !edges.some((edge) => edge.target === node.id);
      })
      .map((node) => node.id);

    function dfs(currentNodeId: string, path: string[]) {
      path.push(currentNodeId);

      if (currentNodeId === endNodeId) {
        paths.push([...path]);
      } else {
        const currentNode = nodes.find((node) => node.id === currentNodeId);
        if (!currentNode) return;

        const connectedEdges = edges.filter((edge) => edge.source === currentNodeId);

        connectedEdges.forEach((edge) => {
          if (!path.includes(edge.target)) {
            dfs(edge.target, path);
          }
        });
      }

      path.pop();
    }

    rootNodeIds.forEach((rootNodeId) => {
      dfs(rootNodeId, []);
    });

    return paths;
  }

  function findLastCommonBranchingNode(paths: string[][]): string | null {
    if (paths.length === 0) {
      return null;
    }
    let lastCommonNode: string | null = null;

    for (let i = 0; i < paths[0].length; i++) {
      const currentNode = paths[0][i];
      const allMatch = paths.every((path) => path[i] === currentNode);

      if (allMatch) {
        lastCommonNode = currentNode;
      } else {
        break;
      }
    }

    return lastCommonNode;
  }

  function extractSubPaths(paths: string[][], branchingNode: string | null): string[][] {
    const subPaths: string[][] = [];

    paths.forEach((path) => {
      const startIndex = branchingNode ? path.indexOf(branchingNode) : -1;
      const endIndex = path.length - 1;
      const sliceStart = startIndex !== -1 ? startIndex + 1 : 0;

      if (sliceStart < endIndex) {
        const subPath = path.slice(sliceStart, endIndex);
        if (subPath.length > 0) {
          subPaths.push(subPath);
        }
      }
    });

    return subPaths;
  }

  function validateMergeInputs(nodes: Node<WorkflowNode>[], edges: Edge[]): string[] {
    const disabledNodes: string[] = [];

    nodes.forEach((node) => {
      if (node.type === 'merge') {
        const allPaths = findAllPaths(node.id, nodes, edges);
        const branchingNode = findLastCommonBranchingNode(allPaths);
        const subPaths = extractSubPaths(allPaths, branchingNode);

        const hasEnabledSubPath = subPaths.some((subPath) =>
          subPath.some((nodeId) => {
            const sourceNode = nodes.find((n) => n.id === nodeId);
            return sourceNode?.data.enabled;
          }),
        );

        let shouldDisableNode = subPaths.length < 2 || !hasEnabledSubPath;

        if ('strategy' in node.data.configuration && node.data.configuration.strategy === 'join') {
          shouldDisableNode = !hasEnabledSubPath;
        } else {
          const enabledSubPathsCount = subPaths.filter((subPath) =>
            subPath.some((nodeId) => {
              const sourceNode = nodes.find((n) => n.id === nodeId);
              return sourceNode?.data.enabled;
            }),
          ).length;

          shouldDisableNode = enabledSubPathsCount < 2;
        }

        if (shouldDisableNode) {
          node.data = { ...node.data, enabled: false };
          disabledNodes.push(node.data.name);
        }
      }
    });

    return disabledNodes;
  }

  const toggleStepEnabled = () => {
    const nodes = reactFlow.getNodes();
    const node = nodes.find((n) => n.id == id);
    if (node) {
      const resultColumn = getResultColumn(node);
      const schemaPropertiesKeys = getSchemaPropertiesKeys(node);
      let disabledNodes: string[] = [];
      if (node.data.enabled) {
        if (resultColumn ?? schemaPropertiesKeys) {
          disabledNodes = disableMatchingNodes(nodes, resultColumn, schemaPropertiesKeys);
        }
      }

      node.data = { ...data, enabled: !data.enabled };
      reactFlow.setNodes(nodes);

      if (!node.data.enabled) {
        const edges = reactFlow.getEdges();
        const mergeDisabledNodes = validateMergeInputs(nodes, edges);
        disabledNodes = [...disabledNodes, ...mergeDisabledNodes];
      }

      if (disabledNodes.length > 0) {
        const message = `Nodes Disabled:\n${disabledNodes.map((node) => `• ${node}`).join('\n')}\n\nThese nodes were disabled due to their dependency on the currently disabled node.`;

        toast.info(message, {
          style: {
            whiteSpace: 'pre-wrap',
          },
        });
      }
    }
    handleCloseContextMenu();
  };

  const renderStatusIndicator = () => {
    if (!isStepEnabled) return null;

    if (isNodeCompleted)
      return !hasWarnings ? (
        <FaCircleCheck color={theme.palette.success.light} />
      ) : (
        <Tooltip title={<NodeIssueTooltip severity="warning" title="Warnings" messages={warnings ?? []} />}>
          <FaTriangleExclamation color={theme.palette.warning.light} />
        </Tooltip>
      );
    if (isNodeRunning) return <FaArrowsRotateAnimated color={theme.palette.info.light} />;
    if (isNodeFailed) return <FaTimesCircle color={theme.palette.error.light} />;
    if (isNodeNotExecuted) return <FaCircleQuestion color={theme.palette.grey.A200} />;
    if (isNodeNew) return <FaHourglassStart color={theme.palette.grey.A200} />;
    if (isNodeCancelled) return <FaBan color={theme.palette.grey.A200} />;

    return null;
  };

  const getTooltipMessage = () => {
    if (!isStepEnabled) {
      return '[Disabled] Step was disabled during execution.';
    }

    if (isExecutionCleanedUp) {
      return '[Cleaned Up] Execution data has been cleaned up.';
    }

    if (isNodeCompleted) {
      return '';
    } else if (isNodeFailed) {
      return '[Failed] Output unavailable. Check logs or rerun workflow.';
    } else if (isNodeRunning) {
      return '[In Progress] Output will be available once completed.';
    } else if (isNodeNotExecuted) {
      return '[Skipped] Not executed due to previous step failure or cancellation.';
    } else if (isNodeCancelled) {
      return '[Cancelled] Step was cancelled during execution.';
    }

    return '';
  };

  const handleCloseLogs = () => {
    showLogs(false);
  };

  const handleCloseNodeOutput = () => {
    showNodeOutput(false);
  };

  const handleShowTestData = () => {
    setContextMenuPosition(null);
    showTestData(true);
  };

  const handleShowExpectedColumns = () => {
    setContextMenuPosition(null);
    showExpectedColumns(true);
  };

  const handleSubmitTestData = (testData?: WorkflowNodeTestData) => {
    const node = reactFlow.getNode(id);
    if (node) {
      node.data = { ...node.data, test_data: testData };
      reactFlow.setNodes((nodes) => [...nodes.filter((n) => n.id != id), node]);
    }
    showTestData(false);
  };

  const handleSubmitExpectedColumns = (expectedColumns?: WorkflowNodeExpectedColumns) => {
    const node = reactFlow.getNode(id);
    if (node) {
      node.data = { ...node.data, expected_columns: expectedColumns };
      reactFlow.setNodes((nodes) => [...nodes.filter((n) => n.id != id), node]);
    }
    showExpectedColumns(false);
  };

  const handleShowTokenUsage = () => {
    setContextMenuPosition(null);
    showTokenUsage(true);
  };

  const handleCloseTokenUsage = () => {
    showTokenUsage(false);
  };

  return (
    <div className="flex flex-col items-center justify-center relative" title={id}>
      <LogsDialog
        onClose={handleCloseLogs}
        filename={`${data.name}-${nodeStatusObj?.trigger_time}`}
        nodeStatus={nodeStatus}
        executionId={workflowExecutionId ?? ''}
        nodeId={id}
      />
      <OutputDialog
        node={data}
        onClose={handleCloseNodeOutput}
        workflowExecutionId={workflowExecutionId!}
        nodeStatus={nodeStatus}
      />
      <TestDataDialog
        // @ts-expect-error: incompatible onSubmit args
        onSubmit={handleSubmitTestData}
        onClose={() => showTestData(false)}
        initialData={data.test_data}
        nodeType={type as WorkflowNodeType}
        defaultColumn={
          data.configuration && 'output_column' in data.configuration ? data.configuration.output_column : undefined
        }
      />
      <ExpectedColumnsDialog
        // @ts-expect-error: incompatible onSubmit args
        onSubmit={handleSubmitExpectedColumns}
        onClose={() => showExpectedColumns(false)}
        initialData={data.expected_columns}
      />
      <ViewTokenUsageDialog onClose={handleCloseTokenUsage} tokenUsage={tokenUsage} nodeTokenUsage={nodeTokenUsage} />
      <div className="flex flex-col items-center justify-center">
        <CustomNodeDetailsTooltip
          id={id}
          type={type}
          node={data}
          open={tooltipOpen}
          onOpen={handleOpenTooltip}
          onClose={() => setTooltipOpen(false)}
        >
          <NodeCard
            error={!!nodeErrors?.length}
            className={`relative overflow-visible ${type === 'loop_entry' || type == 'loop_exit' ? 'border-4 border-brand-sky rounded-full' : ''}`}
            elevation={0}
            progress={isNodeRunning || isNodeNew || isNodeCancelled ? nodeProgress : undefined}
            statusColor={executionStatusColor}
            statusPulse={isNodeRunning}
            color={isStepEnabled ? color : '#6B7A99'}
            style={{
              boxShadow: contextMenuPosition
                ? 'inset 0px 0px 4px 1px rgba(0, 0, 0, 1)'
                : selected
                  ? '0 0 0 3px rgba(76, 96, 201, 0.5), 0 6px 22px rgba(76, 96, 201, 0.45)'
                  : '',
              filter: isNodeNotExecuted ? 'grayscale(90%)' : '',
            }}
            onClick={handleNodeClick}
            onContextMenu={handleNodeContextMenu}
          >
            {icon}
            {readOnly && renderStatusIndicator() && (
              <NodeBadge style={{ bottom: 4, left: 4, width: 20 }}>{renderStatusIndicator()}</NodeBadge>
            )}
            {(nodeStatusObj ? nodeStatusObj.test_mode : true) && !!data.test_data && (
              <TestDataIcon type={data.test_data.type} validationError={validationError} />
            )}
            {(isNodeRunning || isNodeCancelled) && (
              <NodeBadge style={{ top: 4, left: 4 }}>{`${nodeProgress}%`}</NodeBadge>
            )}
            {!readOnly && <RemoveElementBtn onClick={onRemoveButtonClick} className="absolute -top-2 -right-2" />}
            {!readOnly && offlineIssue && (
              <Tooltip
                title={
                  offlineIssue.source === 'node-conditional'
                    ? `Requires a local model in offline mode - ${offlineIssue.reason}`
                    : `Unavailable in offline mode - ${offlineIssue.reason}`
                }
                placement="top"
              >
                <Box
                  className="absolute -top-2 -left-2 rounded-full p-1 flex"
                  sx={(t) => ({
                    backgroundColor: t.palette.background.paper,
                    border: `1px solid ${alpha(t.palette.text.secondary, 0.45)}`,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.45)',
                  })}
                >
                  <CloudOffIcon
                    sx={{ fontSize: 16 }}
                    color={offlineIssue.source === 'node-unavailable' ? 'error' : 'warning'}
                  />
                </Box>
              </Tooltip>
            )}
            {source && <EdgeHandle type="source" position={Position.Right} isConnectable={!readOnly} />}
            {target && (
              <EdgeHandle type="target" position={Position.Left} isValid={isValid} isConnectable={!readOnly} />
            )}
            {!isStepEnabled && (
              <div
                style={{
                  position: 'absolute',
                  top: '60%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '2.2em',
                  color: 'rgba(255, 0, 0, 0.5)',
                }}
              >
                <FaBan />
              </div>
            )}
          </NodeCard>
        </CustomNodeDetailsTooltip>
        <Typography
          className="absolute whitespace-nowrap"
          style={{
            bottom: '-25px',
          }}
          sx={{ fontWeight: 500, fontSize: '0.8125rem' }}
        >
          {nodeName}
        </Typography>

        <StepRemovalConfirmationDialog
          title="Remove step"
          onConfirm={handleStepRemoval}
          onCancel={() => showConfirmationDialog(false)}
          buttonsSettings={{ confirmText: 'Remove', confirmColor: 'error' }}
        >
          <Typography>Are you sure you want to remove this step?</Typography>
        </StepRemovalConfirmationDialog>
      </div>
      <Menu
        open={contextMenuPosition !== null}
        onClose={handleCloseContextMenu}
        onContextMenu={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition !== null ? { top: contextMenuPosition.y, left: contextMenuPosition.x } : undefined
        }
      >
        <Tooltip title={getTooltipMessage()}>
          <div>
            {!isEmbedWorkflowNode && readOnly && (
              <MenuItem disabled={!canShowOutput || isExecutionCleanedUp} onClick={showNodeOutputDetails}>
                <ListItemIcon>
                  <TableChart fontSize="small" />
                </ListItemIcon>
                Show step output
              </MenuItem>
            )}
            {isEmbedWorkflowNode && readOnly && (
              <MenuItem
                disabled={!subworkflowOutputData}
                onClick={() =>
                  void navigate(
                    `/projects/${projectId}/workflows/${subworkflowOutputData?.child_workflow_id}/canvas/revision/${subworkflowOutputData?.child_workflow_revision}/executions/${subworkflowOutputData?.child_execution_id}`,
                  )
                }
              >
                <ListItemIcon>
                  <AccountTree fontSize="small" />
                </ListItemIcon>
                View subworkflow execution
              </MenuItem>
            )}
            {readOnly && (
              <MenuItem disabled={!logFileId || isExecutionCleanedUp} onClick={handleShowLogs}>
                <ListItemIcon>
                  <Comment fontSize="small" />
                </ListItemIcon>
                Show step logs
              </MenuItem>
            )}
            {reactFlow && nodeTokenUsage && (
              <MenuItem onClick={handleShowTokenUsage}>
                <ListItemIcon>
                  <BarChart fontSize="small" />
                </ListItemIcon>
                Show token usage
              </MenuItem>
            )}
            {!readOnly && (
              <div>
                <MenuItem onClick={handleCopyNode}>
                  <ListItemIcon>
                    <ContentCopy fontSize="small" />
                  </ListItemIcon>
                  Copy step
                </MenuItem>
                <MenuItem
                  disabled={nodeTypesThatCantBeDisabled.includes(type as WorkflowNodeType)}
                  onClick={toggleStepEnabled}
                >
                  <ListItemIcon>
                    <Block fontSize="small" />
                  </ListItemIcon>
                  {isStepEnabled ? 'Disable step' : 'Enable step'}
                </MenuItem>
                <MenuItem onClick={handleShowTestData}>
                  <ListItemIcon>
                    <Science fontSize="small" />
                  </ListItemIcon>
                  {source ? 'Edit test data' : 'Edit expected output'}
                </MenuItem>
                {definesExpectedColumns && (
                  <MenuItem onClick={handleShowExpectedColumns}>
                    <ListItemIcon>
                      <ViewWeek fontSize="small" />
                    </ListItemIcon>
                    Edit expected columns
                  </MenuItem>
                )}
              </div>
            )}
          </div>
        </Tooltip>
      </Menu>
    </div>
  );
}
